import { useMemo } from 'react'
import { BrandKit, ContentPreferences, FeaturedMedia, VideoSource } from 'types'
import { DEFAULT_LANGUAGE } from 'components/Wizards/Wizard/Form/LanguageInputField/LanguageInput'
import {
  RECOMMENDED_HASHTAG_FREQUENCY,
  RECOMMENDED_LOGO_FREQUENCY,
  RECOMMENDED_CTA_FREQUENCY,
  RECOMMENDED_BRAND_KIT_MEDIA_REUSE_WEEKS,
} from './constants'

type Props = {
  brandKit?: BrandKit
}

const calculateDefaultWeight = <T extends string>(type: T, enabledTypes: T[]): number => {
  if (!enabledTypes.includes(type)) return 0
  const equalWeight = Math.floor(100 / enabledTypes.length)
  const remainder = 100 - equalWeight * enabledTypes.length
  return equalWeight + (enabledTypes[0] === type ? remainder : 0)
}

export type Output = {
  preferences: CompleteContentPreferences
}

type CompleteContentPreferences = Omit<
  {
    [K in keyof ContentPreferences]-?: Exclude<ContentPreferences[K], null | undefined>
  },
  'social_without_links_cta_copy' | 'visual_style_id'
> & {
  social_without_links_cta_copy?: string
  visual_style_id: number | null
}

export const useContentPreferencesFields = ({ brandKit }: Props): Output => {
  const featuredMedia: FeaturedMedia[] = useMemo(
    () => brandKit?.content_preferences?.featured_media ?? ['video', 'image'],
    [brandKit],
  )
  const videoSources: VideoSource[] = useMemo(
    () => brandKit?.content_preferences?.video_sources ?? ['brand_kit_stock'],
    [brandKit],
  )

  const preferences = useMemo(
    () => ({
      language: brandKit?.content_preferences?.language ?? DEFAULT_LANGUAGE,
      topics_to_avoid: brandKit?.content_preferences?.topics_to_avoid ?? [],
      words_and_phrases_to_avoid: brandKit?.content_preferences?.words_and_phrases_to_avoid ?? [],
      blog_keywords: brandKit?.brand_profile?.keywords ?? [],
      visual_style_id: brandKit?.brand_visual_kit?.visual_style_id ?? null,
      hashtags_to_exclude: brandKit?.content_preferences?.hashtags_to_exclude ?? [],
      hashtags_to_include: brandKit?.content_preferences?.hashtags_to_include ?? [],
      hashtag_frequency: brandKit?.content_preferences?.hashtag_frequency ?? RECOMMENDED_HASHTAG_FREQUENCY,
      logo_frequency: brandKit?.content_preferences?.logo_frequency ?? RECOMMENDED_LOGO_FREQUENCY,
      call_to_action_links: brandKit?.content_preferences?.call_to_action_links ?? [],
      call_to_action_copy: brandKit?.content_preferences?.call_to_action_copy ?? '',
      call_to_action_frequency: brandKit?.content_preferences?.call_to_action_frequency ?? RECOMMENDED_CTA_FREQUENCY,
      social_cta_frequency: brandKit?.content_preferences?.social_cta_frequency ?? RECOMMENDED_CTA_FREQUENCY,
      blog_cta_frequency: brandKit?.content_preferences?.blog_cta_frequency ?? RECOMMENDED_CTA_FREQUENCY,
      social_cta_link: brandKit?.content_preferences?.social_cta_link ?? '',
      social_cta_copy: brandKit?.content_preferences?.social_cta_copy ?? '',
      social_without_links_cta_copy: brandKit?.content_preferences?.social_without_links_cta_copy ?? undefined,
      apply_visual_style: brandKit?.content_preferences?.apply_visual_style ?? false,
      blog_cta_link: brandKit?.content_preferences?.blog_cta_link ?? '',
      blog_cta_copy: brandKit?.content_preferences?.blog_cta_copy ?? '',
      domains_to_avoid: brandKit?.content_preferences?.domains_to_avoid ?? [],
      media_priority: brandKit?.content_preferences?.media_priority ?? 'brand_kit_first',
      image_media_priority: brandKit?.content_preferences?.image_media_priority ?? 'brand_kit_first',
      video_media_priority: brandKit?.content_preferences?.video_media_priority ?? 'brand_kit_first',
      featured_media: featuredMedia,
      featured_media_video_weight:
        brandKit?.content_preferences?.featured_media_video_weight ?? calculateDefaultWeight('video', featuredMedia),
      featured_media_image_weight:
        brandKit?.content_preferences?.featured_media_image_weight ?? calculateDefaultWeight('image', featuredMedia),
      featured_media_text_weight:
        brandKit?.content_preferences?.featured_media_text_weight ?? calculateDefaultWeight('text', featuredMedia),
      video_sources: videoSources,
      video_source_brand_kit_stock_weight:
        brandKit?.content_preferences?.video_source_brand_kit_stock_weight ??
        calculateDefaultWeight('brand_kit_stock', videoSources),
      video_source_animated_image_weight:
        brandKit?.content_preferences?.video_source_animated_image_weight ??
        calculateDefaultWeight('animated_image', videoSources),
      video_templates: brandKit?.content_preferences?.video_templates ?? ['video_only', 'video_with_text_and_color'],
      image_templates: brandKit?.content_preferences?.image_templates ?? [
        'image_only',
        'image_with_text',
        'image_with_text_and_color',
        'image_with_text_and_logo',
      ],
      videos_include_music: brandKit?.content_preferences?.videos_include_music ?? true,
      blog_always_include_images: brandKit?.content_preferences?.blog_always_include_images ?? true,
      blog_always_include_external_links: brandKit?.content_preferences?.blog_always_include_external_links ?? true,
      brand_kit_media_reuse_weeks:
        brandKit?.content_preferences?.brand_kit_media_reuse_weeks ?? RECOMMENDED_BRAND_KIT_MEDIA_REUSE_WEEKS,
      brand_kit_image_reuse_weeks:
        brandKit?.content_preferences?.brand_kit_image_reuse_weeks ?? RECOMMENDED_BRAND_KIT_MEDIA_REUSE_WEEKS,
      brand_kit_video_reuse_weeks:
        brandKit?.content_preferences?.brand_kit_video_reuse_weeks ?? RECOMMENDED_BRAND_KIT_MEDIA_REUSE_WEEKS,
      primary_market_locations: brandKit?.content_preferences?.primary_market_locations ?? [],
      audience_min_age: brandKit?.content_preferences?.audience_min_age ?? 18,
      audience_max_age: brandKit?.content_preferences?.audience_max_age ?? 100,
      audience_gender: brandKit?.content_preferences?.audience_gender ?? 'all',
      representation_age_range: brandKit?.content_preferences?.representation_age_range ?? '18-24',
      representation_gender: brandKit?.content_preferences?.representation_gender ?? 'all',
      representation_culture: brandKit?.content_preferences?.representation_culture ?? 'multicultural_diverse',
      representation_details: brandKit?.content_preferences?.representation_details ?? '',
    }),
    [brandKit, featuredMedia, videoSources],
  )

  return { preferences }
}
